import { useAtomValue, useSetAtom } from 'jotai';

import {
	type AppliedFilterItem,
	FiltersDrawerAppliedFiltersSection,
} from '@common/components/FiltersDrawer';

import { allAppliedFiltersAtom } from '../../atoms/leadsAppliedFiltersAtoms';
import {
	accountFilterAtom,
	clearAllFiltersAtom,
	leadTypeFilterAtom,
	pipelineFilterAtom,
	preQualStatusFilterAtom,
	stageFilterAtom,
	updateAccountFilterAtom,
	updateActionRequiredFilterAtom,
	updateDateCreatedFilterAtom,
	updateDealAmountRangeFilterAtom,
	updateLastUpdatedFilterAtom,
	updateLeadTypeFilterAtom,
	updatePipelineFilterAtom,
	updatePreQualStatusFilterAtom,
	updateSearchValueAtom,
	updateStageFilterAtom,
	updateVehicleModelsFilterAtom,
	vehicleModelsFilterAtom,
} from '../../atoms/leadsAtoms';
import { useAccountNameMap } from '../../hooks/useAccountNameMap';

/**
 * LeadsFiltersDrawerAppliedFiltersSection component that displays currently applied filters
 * with the ability to remove individual filters or clear all filters
 */
export function LeadsFiltersDrawerAppliedFiltersSection() {
	const allAppliedFilters = useAtomValue(allAppliedFiltersAtom);
	const clearAllFilters = useSetAtom(clearAllFiltersAtom);
	const setSearchValue = useSetAtom(updateSearchValueAtom);
	const setActionRequiredFilter = useSetAtom(updateActionRequiredFilterAtom);
	const setPipelineFilter = useSetAtom(updatePipelineFilterAtom);
	const setPreQualStatusFilter = useSetAtom(updatePreQualStatusFilterAtom);
	const setStages = useSetAtom(updateStageFilterAtom);
	const setLeadTypes = useSetAtom(updateLeadTypeFilterAtom);
	const setVehicleModels = useSetAtom(updateVehicleModelsFilterAtom);
	const setDealAmountRange = useSetAtom(updateDealAmountRangeFilterAtom);
	const setAccounts = useSetAtom(updateAccountFilterAtom);
	const setDateCreatedFilter = useSetAtom(updateDateCreatedFilterAtom);
	const setLastUpdatedFilter = useSetAtom(updateLastUpdatedFilterAtom);

	// Read current filter values for removal operations
	const currentPipelines = useAtomValue(pipelineFilterAtom);
	const currentStages = useAtomValue(stageFilterAtom);
	const currentLeadTypes = useAtomValue(leadTypeFilterAtom);
	const currentVehicleModels = useAtomValue(vehicleModelsFilterAtom);
	const currentAccounts = useAtomValue(accountFilterAtom);
	const currentPreQualStatuses = useAtomValue(preQualStatusFilterAtom);

	// Map svpId to account names for display
	const accountNameMap = useAccountNameMap();

	// Use the applied filters from the atoms with clear functions
	const allFilters: AppliedFilterItem[] = allAppliedFilters.map((filter) => ({
		...filter,
		value: filter.id.startsWith('account-')
			? (accountNameMap.get(filter.value) ?? filter.value)
			: filter.value,
		clearFunction: getClearFunctionForFilter(filter.id),
	}));

	function getClearFunctionForFilter(filterId: string) {
		// Handle pipeline filters - extract the value from the id
		if (filterId.startsWith('pipeline-')) {
			const value = filterId.replace('pipeline-', '');
			return () => {
				const updatedPipelines = currentPipelines.filter((v) => v !== value);
				setPipelineFilter(updatedPipelines);
			};
		}

		// Handle stage filters - extract the value from the id
		if (filterId.startsWith('stage-')) {
			const value = filterId.replace('stage-', '');
			return () => {
				const updatedStages = currentStages.filter((v) => v !== value);
				setStages(updatedStages);
			};
		}

		// Handle lead type filters - extract the value from the id
		if (filterId.startsWith('lead-type-')) {
			const value = filterId.replace('lead-type-', '');
			return () => {
				const updatedTypes = currentLeadTypes.filter((v) => v !== value);
				setLeadTypes(updatedTypes);
			};
		}

		// Handle vehicle model filters - extract the value from the id
		if (filterId.startsWith('vehicle-')) {
			const value = filterId.replace('vehicle-', '');
			return () => {
				const updatedModels = currentVehicleModels.filter((v) => v !== value);
				setVehicleModels(updatedModels);
			};
		}

		// Handle account filters - extract the value from the id
		if (filterId.startsWith('account-')) {
			const value = filterId.replace('account-', '');
			return () => {
				const updatedAccounts = currentAccounts.filter((v) => v !== value);
				setAccounts(updatedAccounts);
			};
		}

		// Handle prequal status filters - extract the value from the id
		if (filterId.startsWith('prequal-status-')) {
			const value = filterId.replace('prequal-status-', '');
			return () => {
				const updatedStatuses = currentPreQualStatuses.filter((v) => v !== value);
				setPreQualStatusFilter(updatedStatuses);
			};
		}

		// Handle single-value filters
		switch (filterId) {
			case 'search-query':
				return () => setSearchValue('');
			case 'action-required':
				return () => setActionRequiredFilter(false);
			case 'deal-amount':
				return () => setDealAmountRange(null);
			case 'date-created':
				return () => setDateCreatedFilter(null);
			case 'last-updated':
				return () => setLastUpdatedFilter(null);
			default:
				return () => {};
		}
	}

	const handleRemoveFilter = (filterId: string) => {
		const filter = allFilters.find((f) => f.id === filterId);
		if (filter) {
			filter.clearFunction();
		}
	};

	return (
		<FiltersDrawerAppliedFiltersSection
			allAppliedFilters={allFilters}
			clearAllFilters={clearAllFilters}
			onRemoveFilter={handleRemoveFilter}
		/>
	);
}
